import Header from "@/components/common/head";
import NavBar from "@/components/common/navbar";
import type { TokenProps } from "@/components/landing/topTokens";
import { tokens } from "@/utils/dummy";
import { formatToK } from "@/utils/numFormatter";
import useDebounce from "@/utils/useDebounce";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

function Row({ index, style, data }: { index: number; style: any; data: any }) {
  const row = data[index];

  const router = useRouter();

  return (
    <div
      style={style}
      onClick={() => router.push(`/tokens/${row?.id}`)}
      className="tr flex w-full cursor-pointer items-center justify-between rounded-md border-b-[3px] border-black bg-[#151515] py-6 hover:bg-[#151515]/50"
    >
      <div className="td flex md:w-[50%] flex-col items-start gap-3 whitespace-nowrap px-3 py-4 pl-4 text-sm font-medium md:pl-10">
        <div className="flex items-center gap-3">
          <Image
            src={row?.image}
            height={30}
            width={30}
            quality={100}
            alt={row?.name}
            className="object-contain"
          />
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-1">
            <h3 className="text-base font-semibold text-white">{row?.name}</h3>
            <span className="text-xs font-[300] text-white/50">
              ({row?.type})
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-0 lg:hidden">
          <span className="whitespace-nowrap bg-[#151515] text-sm font-[300]">
            Volume: ${formatToK(row?.volume)}
          </span>
          <span className="whitespace-nowrap bg-[#151515] text-sm font-[300]">
            TVL: ${formatToK(row?.tvl)}
          </span>
        </div>
      </div>
      <div className="td md:w-[12%] lg:pr-0 pr-4 whitespace-nowrap py-4 text-base font-[300] md:text-right lg:text-sm">
        ${formatToK(row?.price)}
      </div>
      <div className="td hidden w-[12%] whitespace-nowrap py-4 pl-2 text-sm font-[300] md:text-right lg:block">
        <p
          className={`flex w-full justify-end text-[16px]  ${
            row?.shift < 0 ? "text-red" : "text-green"
          }`}
        >
          {row?.shift < 0 ? (
            <span className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-5 w-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a.75.75 0 01.75.75v6.638l1.96-2.158a.75.75 0 111.08 1.04l-3.25 3.5a.75.75 0 01-1.08 0l-3.25-3.5a.75.75 0 111.08-1.04l1.96 2.158V5.75A.75.75 0 0110 5z"
                  clipRule="evenodd"
                />
              </svg>

              {row?.shift?.toString()?.split("-")[1]}
            </span>
          ) : (
            <span className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-5 w-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10 15a.75.75 0 01-.75-.75V7.612L7.29 9.77a.75.75 0 01-1.08-1.04l3.25-3.5a.75.75 0 011.08 0l3.25 3.5a.75.75 0 11-1.08 1.04l-1.96-2.158v6.638A.75.75 0 0110 15z"
                  clipRule="evenodd"
                />
              </svg>
              {row?.shift}
            </span>
          )}
          %
        </p>
      </div>
      <div className="td hidden w-[12%] whitespace-nowrap py-4 pl-2 text-sm font-[300] md:text-right lg:block">
        ${formatToK(row?.volume)}
      </div>
      <div className="td hidden w-[12%] whitespace-nowrap py-4 pl-2 text-sm font-[300]  md:justify-end md:pr-10 md:text-right lg:block">
        ${formatToK(row?.tvl)}
      </div>
    </div>
  );
}

export default function TokensPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const searchQuery = useSelector(
    (state: { common: { searchQuery: string } }) => state.common.searchQuery
  );

  const debouncedValue = useDebounce(searchQuery, 500);

  const [allTokens, setAllTokens] = useState<TokenProps[]>(tokens);

  useEffect(() => {
    if (allTokens && debouncedValue?.length > 0) {
      const tokensFound: TokenProps[] = [];
      allTokens.forEach((tkn) => {
        if (tkn.name.toLowerCase().includes(debouncedValue.toLowerCase())) {
          tokensFound.push(tkn);
        }
      });
      if (tokensFound.length > 0) {
        setAllTokens(tokensFound);
      } else {
        setAllTokens(tokens);
      }
    }

    if (debouncedValue.length === 0) {
      setAllTokens(tokens);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const [sortOrder, setSortOrder] = useState("desc");

  const [items, setItems] = useState<TokenProps[]>(
    tokens.slice(0, 15).sort((a, b) => b.tvl - a.tvl)
  );

  const fetchMoreData = () => {
    setLoading(true);
    setTimeout(() => {
      setItems((prevItems) =>
        prevItems.concat(tokens.slice(items.length, items.length + 15).sort((a, b) => b.tvl - a.tvl))
      );
      setLoading(false);
    }, 500);
  };

  const data = React.useMemo(() => items, [items]);
  const limit = 1000;
  const hasNextPage = React.useMemo(() => data.length <= limit, [data, limit]);

  const [isMobile, setIsMobile] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [listHeight, setListHeight] = useState(0);

  useEffect(() => {
    setIsReady(true);
    setIsMobile(window.innerWidth < 1024);
    setListHeight(window.innerHeight - 250);
  }, []);

  useEffect(() => {
    // listen for resize, then set state to window width
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setListHeight(window.innerHeight - 250);
    };

    window.addEventListener("resize", handleResize);

    // clean up
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!isReady || listHeight === 0) return null;

  return (
    <>
      <Header />
      <main className={`w-full bg-black px-6 pb-0 md:px-8`}>
        <NavBar />
        <section className="mx-auto mt-48 flex w-full max-w-7xl flex-col">
          <div className="flex w-full justify-between">
            <h2 className="text-xl font-medium text-white">All Tokens</h2>
          </div>

          <div className="relative mt-4 w-full">
            <div
              className="min-w-full table-fixed rounded-lg border-[1px] border-[#1B1B1B]"
              style={{ height: "100%", width: "100%" }}
            >
              <div className="w-full text-sm font-[300] text-[#9D9D9D]">
                <div className="mb-1 flex w-full justify-between border-b-[1px] border-[#1B1B1B] px-2 text-sm font-[300]">
                  <div className="w-[50%] py-3.5 text-left md:pl-10">Name</div>
                  <div className="w-[12%] py-3.5 text-left md:text-right">
                    Price
                  </div>
                  <div className="hidden w-[12%] py-3.5 text-left md:text-right lg:block">
                    Price change
                  </div>
                  <div className="hidden w-[12%] py-3.5 text-left md:text-right lg:block">
                    Volume (24H)
                  </div>
                  <div
                    onClick={() => {
                      if (sortOrder === "desc") {
                        setSortOrder("asc");
                        setItems(items.sort((a, b) => a.tvl - b.tvl));
                      }
                      if (sortOrder === "asc") {
                        setSortOrder("desc");
                        setItems(items.sort((a, b) => b.tvl - a.tvl));
                      }
                    }}
                    className="hidden w-[12%] cursor-pointer py-3.5 text-left md:justify-end md:pr-10 md:text-right lg:flex"
                  >
                    TVL
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`h-5 w-5 transition-all duration-300 ease-in ${
                        sortOrder === "desc" && "rotate-180"
                      }`}
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 5a.75.75 0 01.75.75v6.638l1.96-2.158a.75.75 0 111.08 1.04l-3.25 3.5a.75.75 0 01-1.08 0l-3.25-3.5a.75.75 0 111.08-1.04l1.96 2.158V5.75A.75.75 0 0110 5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="w-full px-2 text-white">
                {isLoading ? (
                  [...Array(10)].map((_, index) => (
                    <div
                      key={index}
                      className="my-2 flex h-[120px] w-full items-center justify-center whitespace-nowrap rounded-l-[6px] rounded-r-[6px] bg-[#151515] px-4 text-sm text-white lg:h-[55px]"
                    >
                      <div className="my-auto h-[60%] w-full animate-pulse rounded-[15px] bg-[#2C2C2C]" />
                    </div>
                  ))
                ) : (
                  <InfiniteLoader
                    isItemLoaded={(index) => index < data.length}
                    itemCount={hasNextPage ? data.length + 1 : data.length}
                    loadMoreItems={fetchMoreData}
                  >
                    {({ onItemsRendered, ref }) => (
                      <FixedSizeList
                        height={listHeight}
                        itemCount={data.length}
                        itemSize={isMobile ? 120 : 60}
                        width={"100%"}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        className="no-scrollbar"
                      >
                        {({ index, style }) => (
                          <Row index={index} style={style} data={data} />
                        )}
                      </FixedSizeList>
                    )}
                  </InfiniteLoader>
                )}
                {tokens.length === 0 && (
                  <div className="mb-1 w-full whitespace-nowrap rounded-md bg-[#2C2C2C] px-4 py-4 text-sm text-white">
                    <h2>No data available</h2>
                  </div>
                )}
              </div>
            </div>
            {loading && (
              <div className="relative my-8 flex w-full items-center">
                <div className="h-[1px] min-h-[1px] w-full bg-[#1B1B1B]" />
                <span className="absolute left-1/2 flex h-12 w-max -translate-x-1/2 items-center justify-center bg-black px-4 text-sm font-medium text-white/50">
                  Loading more...
                </span>
              </div>
            )}
            {!loading && hasNextPage && (
              <div className="relative my-8 flex w-full items-center">
                <div className="h-[1px] min-h-[1px] w-full bg-[#1B1B1B]" />
                <span
                  onClick={() => fetchMoreData()}
                  className="absolute left-1/2 flex h-12 w-max -translate-x-1/2 cursor-pointer items-center justify-center bg-black px-4 text-sm font-medium text-white/50"
                >
                  Load more
                </span>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
